// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';

// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';

// Third party
import { takeUntil } from 'rxjs';

// Local library
import {
  AccountFacade,
  IStatements,
  StatementsService
} from '@amwager/account/data-access';
import { WagerFacade } from '@amwager/wager/data-access';
import {
  MatNotificationService,
  NumberWithCommasPipe,
  StatementPipe,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';
import { IEventsNameAndIdModel } from '@amwager/util-ts';

/**
 * Account statements — date/track/pool filters with totals and report
 * rows. Ported from the Ionic statements view; form + request helpers
 * live in StatementsService.
 */
@Component({
  selector: 'feature-mat-statements',
  templateUrl: './statements.component.html',
  styleUrls: ['./statements.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    NumberWithCommasPipe,
    StatementPipe,
    TranslatePipe
  ]
})
export class StatementsComponent
  extends UnsubscribeObservableService
  implements OnInit, OnDestroy {

  private readonly HOST = 'StatementsComponent';

  readonly poolList = this.statementsService.poolList;
  readonly maxDate = new Date();

  tracks: IEventsNameAndIdModel[] = [];
  getStatementsForm!: FormGroup;
  statements?: IStatements;

  get formVal() { return this.getStatementsForm?.controls; }

  constructor(
    private readonly statementsService: StatementsService,
    private readonly wagerFacade: WagerFacade,
    private readonly accountFacade: AccountFacade,
    private readonly matNotificationService: MatNotificationService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.getStatementsForm = this.statementsService.buildForm();

    // Track dropdown options
    this.wagerFacade.getAllEventsNameAndId(`${this.HOST}:ngOnInit`);
    this.wagerFacade.getAllEventsNameAndId$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((data) => {
        this.tracks = data?.response?.AllAvailableEvents ?? [];
        this.cdRef.markForCheck();
      });

    // Initial statements + subsequent results
    this.fetchStatements(`${this.HOST}:ngOnInit`);
    this.accountFacade.getAccountStatements$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((statements?: IStatements) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const error = (statements?.response as any)?.error;
        if (error) {
          this.statements = undefined;
          this.showAlert('Error', error?.Message);
        } else {
          this.statements = statements;
        }
        this.cdRef.markForCheck();
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Search button — validates the range then dispatches the fetch
   * @returns {void}
   */
  searchStatements(): void {
    if (this.statementsService.isDateRangeInvalid(this.getStatementsForm)) {
      this.showAlert('Error', 'Date To should not be less than Date From.');
      return;
    }
    this.fetchStatements(`${this.HOST}:searchStatements`);
  }

  /**
   * Clear a select control (Track/Pool)
   * @param {'track' | 'pool'} control Control name to reset
   * @returns {void}
   */
  clearControl(control: 'track' | 'pool'): void {
    this.formVal[control]?.setValue('');
  }

  /**
   * trackBy for the track list
   * @param {number} _index Loop index
   * @param {IEventsNameAndIdModel} track Track option
   * @returns {string} Stable key
   */
  trackByEventId(_index: number, track: IEventsNameAndIdModel): string {
    return track?.EventId ?? String(_index);
  }

  /**
   * trackBy for the pool list
   * @param {number} _index Loop index
   * @param {string} pool Pool id
   * @returns {string} Stable key
   */
  trackByPool(_index: number, pool: string): string {
    return pool;
  }

  private fetchStatements(source: string): void {
    this.accountFacade.getAccountStatements(
      source,
      this.statementsService.buildRequest(this.getStatementsForm)
    );
  }

  private showAlert(header: string, message: string): void {
    this.matNotificationService.showAlert(header, message ?? '', '', '', 'Close');
  }
}
